(() => {
  const ROOT_SELECTOR = '[data-aav-homepage]';

  function initRoot(root) {
    if (!root || root.dataset.aavReady === 'true') return;
    root.dataset.aavReady = 'true';

    const cleanups = [];

    const on = (target, type, handler, options) => {
      if (!target) return;

      target.addEventListener(type, handler, options);

      cleanups.push(() => {
        target.removeEventListener(type, handler, options);
      });
    };

    const searchForm = root.querySelector('[data-aav-search-form]');
    const searchInput = root.querySelector('[data-aav-search-input]');
    const searchStatus = root.querySelector('[data-aav-search-status]');
    const searchResults = root.querySelector('[data-aav-search-results]');
    const searchButton = root.querySelector('[data-aav-search-submit]');

    let searchTimer = null;
    let searchController = null;

    const hideResults = () => {
      if (!searchResults || !searchInput || !searchStatus) return;

      searchResults.hidden = true;
      searchResults.removeAttribute('aria-busy');
      searchResults.replaceChildren();
      searchStatus.textContent = '';
    };

    const makeSearchUrl = (query) => {
      const fallbackPath = root.dataset.aavSearchUrl || '/search';
      const fallbackUrl = new URL(fallbackPath, window.location.origin);

      fallbackUrl.searchParams.set('q', query);
      fallbackUrl.searchParams.set('type', 'article');
      fallbackUrl.searchParams.set('options[prefix]', 'last');

      return fallbackUrl;
    };

    const renderMessage = (message, query, includeFallback) => {
      if (!searchResults || !searchInput || !searchStatus) return;

      searchStatus.textContent = message;
      searchResults.replaceChildren();
      searchResults.hidden = !includeFallback;

      if (includeFallback) {
        const links = document.createElement('div');
        links.className = 'aav-search-result-grid';

        const fallback = document.createElement('a');
        fallback.href = makeSearchUrl(query).toString();
        fallback.dataset.aavCta = 'article_search_fallback';

        const title = document.createElement('strong');
        title.textContent = `Search Shopify for “${query}”`;

        const action = document.createElement('span');
        action.textContent = 'View full article search';

        fallback.append(title, action);
        links.append(fallback);
        searchResults.append(links);

        searchResults.hidden = false;
      }
    };

    const renderArticles = (articles, query) => {
      if (!searchResults || !searchInput || !searchStatus) return;

      const safeArticles = Array.isArray(articles)
        ? articles.filter((article) => article && article.title && article.url)
        : [];

      if (!safeArticles.length) {
        renderMessage(
          `No article suggestions found for “${query}”.`,
          query,
          true
        );
        return;
      }

      searchStatus.textContent =
        `${safeArticles.length} article suggestion` +
        `${safeArticles.length === 1 ? '' : 's'} found.`;

      const grid = document.createElement('div');
      grid.className = 'aav-search-result-grid';

      safeArticles.forEach((article, index) => {
        let articleUrl;

        try {
          articleUrl = new URL(article.url, window.location.origin);
        } catch (error) {
          return;
        }

        if (articleUrl.origin !== window.location.origin) return;

        const link = document.createElement('a');
        link.href = articleUrl.toString();
        link.dataset.aavCta = `article_search_result_${index + 1}`;

        const title = document.createElement('strong');
        title.textContent = article.title;

        const action = document.createElement('span');
        action.textContent = 'Read article';

        link.append(title, action);
        grid.append(link);
      });

      if (!grid.childElementCount) {
        renderMessage(
          `No safe article links were returned for “${query}”.`,
          query,
          true
        );
        return;
      }

      const allResults = document.createElement('a');
      allResults.href = makeSearchUrl(query).toString();
      allResults.dataset.aavCta = 'article_search_all_results';

      const allResultsTitle = document.createElement('strong');
      allResultsTitle.textContent = `View all article results for “${query}”`;

      const allResultsAction = document.createElement('span');
      allResultsAction.textContent = 'Open full article search';

      allResults.append(allResultsTitle, allResultsAction);
      grid.append(allResults);

      searchResults.replaceChildren(grid);
      searchResults.hidden = false;
    };

    const searchArticles = async (rawQuery) => {
      const query = String(rawQuery || '').trim();

      if (
        !searchInput ||
        !searchResults ||
        !searchStatus ||
        query.length < 2
      ) {
        if (searchController) {
          searchController.abort();
        }

        searchController = null;

        if (searchButton) {
          searchButton.disabled = false;
        }

        hideResults();
        return;
      }

      if (searchController) {
        searchController.abort();
      }

      const controller = new AbortController();
      searchController = controller;

      renderMessage(`Searching articles for “${query}”...`, query, false);

      searchResults.setAttribute('aria-busy', 'true');

      if (searchButton) {
        searchButton.disabled = true;
      }

      try {
        let endpointPath =
          root.dataset.aavPredictiveUrl || '/search/suggest';

        if (!endpointPath.endsWith('.json')) {
          endpointPath += '.json';
        }

        const endpoint = new URL(
          endpointPath,
          window.location.origin
        );

        endpoint.searchParams.set('q', query);
        endpoint.searchParams.set(
          'resources[type]',
          'article'
        );
        endpoint.searchParams.set(
          'resources[limit]',
          '8'
        );
        endpoint.searchParams.set(
          'resources[options][fields]',
          'title,body,tag,author'
        );

        const response = await fetch(endpoint.toString(), {
          headers: {
            Accept: 'application/json'
          },
          signal: controller.signal
        });

        if (!response.ok) {
          throw new Error(`Search returned ${response.status}`);
        }

        const payload = await response.json();

        if (
          searchController !== controller ||
          searchInput.value.trim() !== query
        ) {
          return;
        }

        const articles =
          payload &&
          payload.resources &&
          payload.resources.results
            ? payload.resources.results.articles
            : [];

        renderArticles(articles, query);
      } catch (error) {
        if (error && error.name === 'AbortError') {
          return;
        }

        if (
          searchController !== controller ||
          searchInput.value.trim() !== query
        ) {
          return;
        }

        renderMessage(
          'Article suggestions are temporarily unavailable.',
          query,
          true
        );
      } finally {
        if (searchController === controller) {
          searchController = null;

          if (searchButton) {
            searchButton.disabled = false;
          }

          searchResults.removeAttribute('aria-busy');
        }
      }
    };

    const queueSearch = (query, immediate) => {
      if (searchTimer) {
        window.clearTimeout(searchTimer);
        searchTimer = null;
      }

      if (immediate) {
        searchArticles(query);
        return;
      }

      searchTimer = window.setTimeout(() => {
        searchTimer = null;
        searchArticles(query);
      }, 275);
    };

    if (
      searchForm &&
      searchInput &&
      searchResults &&
      searchStatus
    ) {
      on(searchForm, 'submit', (event) => {
        const query = searchInput.value.trim();

        if (query.length < 2) {
          return;
        }

        event.preventDefault();
        queueSearch(query, true);
      });

      on(searchInput, 'input', () => {
        const query = searchInput.value.trim();

        if (query.length < 2) {
          if (searchTimer) {
            window.clearTimeout(searchTimer);
          }

          searchTimer = null;

          if (searchController) {
            searchController.abort();
          }

          searchController = null;

          if (searchButton) {
            searchButton.disabled = false;
          }

          hideResults();
          return;
        }

        if (searchController) {
          searchController.abort();
        }

        searchController = null;

        if (searchButton) {
          searchButton.disabled = false;
        }

        hideResults();
        queueSearch(query, false);
      });

      on(searchInput, 'keydown', (event) => {
        if (event.key === 'Escape') {
          if (searchTimer) {
            window.clearTimeout(searchTimer);
          }

          searchTimer = null;

          if (searchController) {
            searchController.abort();
          }

          searchController = null;

          if (searchButton) {
            searchButton.disabled = false;
          }

          hideResults();
        }
      });
    }

    /* ============================================================
       Smooth scroll for internal homepage anchor links
    ============================================================ */

    const smoothScrollHandler = (event) => {
      const link = event.target.closest('a[href^="#"]');

      if (!link || !root.contains(link)) {
        return;
      }

      const targetId = link.getAttribute('href');

      if (!targetId || targetId === '#') {
        return;
      }

      let target;

      try {
        target = document.querySelector(targetId);
      } catch (error) {
        return;
      }

      if (!target) {
        return;
      }

      event.preventDefault();

      const headerOffset = 90;

      const targetPosition =
        target.getBoundingClientRect().top +
        window.pageYOffset -
        headerOffset;

      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });

      if (
        window.history &&
        typeof window.history.replaceState === 'function'
      ) {
        window.history.replaceState(
          null,
          '',
          targetId
        );
      }
    };

    on(root, 'click', smoothScrollHandler);

    /* ============================================================
       CTA tracking
    ============================================================ */

    const trackCta = (event) => {
      const link = event.target.closest('[data-aav-cta]');

      if (
        !link ||
        !root.contains(link) ||
        !Array.isArray(window.dataLayer)
      ) {
        return;
      }

      window.dataLayer.push({
        event: 'aav_cta_click',
        aav_cta: link.dataset.aavCta
      });
    };

    on(root, 'click', trackCta);

    /* ============================================================
       Mobile sticky shop bar
    ============================================================ */

    const buybar = root.querySelector('[data-aav-buybar]');
    const hero = root.querySelector('[data-aav-hero]');
    const shop = root.querySelector('[data-aav-shop]');
    const finalSection = root.querySelector('[data-aav-final]');

    const visible = {
      hero: true,
      shop: false,
      final: false
    };

    let observer = null;

    const intersectsViewport = (element) => {
      if (!element) {
        return false;
      }

      const rect = element.getBoundingClientRect();

      return (
        rect.bottom > 0 &&
        rect.top < window.innerHeight
      );
    };

    const updateBuybar = () => {
      if (!buybar) {
        return;
      }

      if (!observer) {
        visible.hero = intersectsViewport(hero);
        visible.shop = intersectsViewport(shop);
        visible.final = intersectsViewport(finalSection);
      }

      const mobile = window
        .matchMedia('(max-width: 749px)')
        .matches;

      const pastOpening =
        window.scrollY >
        window.innerHeight * 0.6;

      const pastHomepage =
        root.getBoundingClientRect().bottom <=
        window.innerHeight + 96;

      const show =
        mobile &&
        pastOpening &&
        !visible.hero &&
        !visible.shop &&
        !visible.final &&
        !pastHomepage;

      buybar.hidden = !show;
      buybar.inert = !show;

      root.classList.toggle(
        'aav-buybar-active',
        show
      );
    };

    if (buybar) {
      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.target === hero) {
                visible.hero = entry.isIntersecting;
              }

              if (entry.target === shop) {
                visible.shop = entry.isIntersecting;
              }

              if (entry.target === finalSection) {
                visible.final = entry.isIntersecting;
              }
            });

            updateBuybar();
          },
          {
            threshold: 0.05
          }
        );

        [hero, shop, finalSection]
          .filter(Boolean)
          .forEach((element) => {
            observer.observe(element);
          });

        cleanups.push(() => {
          observer.disconnect();
        });
      }

      on(
        window,
        'scroll',
        updateBuybar,
        {
          passive: true
        }
      );

      on(
        window,
        'resize',
        updateBuybar,
        {
          passive: true
        }
      );

      updateBuybar();
    }

    /* ============================================================
       Cleanup
    ============================================================ */

    root.aavCleanup = () => {
      if (searchTimer) {
        window.clearTimeout(searchTimer);
      }

      if (searchController) {
        searchController.abort();
      }

      cleanups.forEach((cleanup) => {
        cleanup();
      });

      if (buybar) {
        buybar.hidden = true;
        buybar.inert = true;
      }

      root.classList.remove(
        'aav-buybar-active'
      );

      delete root.dataset.aavReady;
      delete root.aavCleanup;
    };
  }

  const initWithin = (container) => {
    if (!container) {
      return;
    }

    if (
      container.matches &&
      container.matches(ROOT_SELECTOR)
    ) {
      initRoot(container);
    }

    container
      .querySelectorAll(ROOT_SELECTOR)
      .forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initWithin(document);
      },
      {
        once: true
      }
    );
  } else {
    initWithin(document);
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initWithin(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const root =
        event.target.matches &&
        event.target.matches(ROOT_SELECTOR)
          ? event.target
          : event.target.querySelector(
              ROOT_SELECTOR
            );

      if (
        root &&
        typeof root.aavCleanup === 'function'
      ) {
        root.aavCleanup();
      }
    }
  );
})();
